import React from "react";
import { Image, View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { FileText, MessageSquare, Plus } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { DocumentRow, MoonDateField } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { CancerDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import { useMedicalHistoryTheme } from "../../../../theme/MedicalHistoryTheme";

const cancerIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;

export function CancerDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("CancerDetails");
  const theme = useMedicalHistoryTheme();

  // If no cancers exist yet, add an empty one
  const cancers =
    state.cancers.length > 0
      ? state.cancers
      : [
          {
            cancer: null,
            diagnosisDate: null,
            inRemission: null,
            treatments: [],
            treatmentIdsToDelete: [],
          },
        ];

  const updateCancer = (index: number, updates: Partial<CancerDetail>) => {
    const updated = cancers.map((c, i) =>
      i === index ? { ...c, ...updates } : c,
    );
    updateState({ cancers: updated });
  };

  const addAnotherCancer = () => {
    const newCancer: CancerDetail = {
      cancer: null,
      diagnosisDate: null,
      inRemission: null,
      treatments: [],
      treatmentIdsToDelete: [],
    };
    updateState({ cancers: [...cancers, newCancer] });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("CancerDetails");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid = cancers.every((c) => c.cancer != null && c.cancer.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.medical.disease_history.cancer_details.title")}
      subtitle={t(
        "med_history.medical.disease_history.cancer_details.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        {cancers.map((cancer, index) => (
          <View
            key={`cancer-${index}`}
            style={[GLASS_CARD_STYLE, styles.detailCard]}
          >
            <View style={styles.cardHeader}>
              <Text style={[styles.cardTitle, { color: theme.text }]}>
                {cancers.length > 1
                  ? t(
                      "med_history.medical.disease_history.cancer_details.card_label_indexed",
                      { params: { index: String(index + 1) } },
                    )
                  : t(
                      "med_history.medical.disease_history.cancer_details.card_label",
                    )}
              </Text>
            </View>

            <IconInput
              icon={FileText}
              label={t(
                "med_history.medical.disease_history.cancer_details.type_label",
              )}
              placeholder={t(
                "med_history.medical.disease_history.cancer_details.type_placeholder",
              )}
              value={cancer.cancer ?? ""}
              onChangeText={(text) =>
                updateCancer(index, { cancer: text || null })
              }
            />

            <MoonDateField
              label={t(
                "med_history.medical.disease_history.cancer_details.diagnosis_date_label",
              )}
              placeholder={t(
                "med_history.medical.disease_history.cancer_details.diagnosis_date_placeholder",
              )}
              value={cancer.diagnosisDate ?? ""}
              onChange={(iso) =>
                updateCancer(index, { diagnosisDate: iso || null })
              }
            />

            <IconInput
              icon={MessageSquare}
              label={t(
                "med_history.medical.disease_history.cancer_details.remission_label",
              )}
              placeholder={t(
                "med_history.medical.disease_history.cancer_details.remission_placeholder",
              )}
              value={cancer.inRemission ?? ""}
              onChangeText={(text) =>
                updateCancer(index, { inRemission: text || null })
              }
            />
          </View>
        ))}

        <DocumentRow
          onPress={addAnotherCancer}
          accessibilityLabel={t(
            "med_history.medical.disease_history.cancer_details.add_another",
          )}
          leading={
            <Image
              source={cancerIllustration}
              resizeMode="contain"
              style={styles.addIllustration}
            />
          }
          title={t(
            "med_history.medical.disease_history.cancer_details.add_another",
          )}
          trailing={
            <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
          }
          showChevron={false}
          style={styles.addRow}
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  detailCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});
